import {
  AlertTriangle,
  Calendar,
  ChevronDown,
  ChevronUp,
  DollarSign,
  Percent,
  RefreshCcw,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, SelectButtonsNew } from '@/components/ui';
import { ModalContentWrapper } from '@/components/ui';
import { useRestructureLoan } from '@/hooks/use-loans';
import { calculateAmortization } from '@/lib/amortization';
import { handleMutationError } from '@/lib/error-handling';
import { useAuthStore } from '@/stores/auth-store';
import { toast } from '@/stores/toast-store';

interface Props {
  loan: {
    id: string;
    loanNumber: string;
    status: string;
    principal: string | number;
    interestRate: string | number;
    defaultRate?: string | number | null;
    termMonths: number;
    paymentFrequency?: string;
    loanType: string;
    borrower?: { name: string };
    ledger?: {
      balances?: Array<{
        indicator: string;
        debitBalance: string | number;
        creditBalance: string | number;
      }>;
    };
  };
  open: boolean;
  onClose: () => void;
}

function fmt(num: number): string {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(num);
}

function fmtPrecise(num: number): string {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
}

const FREQ_OPTIONS = [
  { value: 'WEEKLY', label: 'Weekly' },
  { value: 'BIWEEKLY', label: 'Fortnightly' },
  { value: 'MONTHLY', label: 'Monthly' },
  { value: 'QUARTERLY', label: 'Quarterly' },
] as const;

type PaymentFrequency = (typeof FREQ_OPTIONS)[number]['value'];

function normalizeFrequency(value?: string): PaymentFrequency {
  return FREQ_OPTIONS.some(option => option.value === value)
    ? (value as PaymentFrequency)
    : 'MONTHLY';
}

export function RestructureModal({ loan, open, onClose }: Props) {
  const user = useAuthStore(s => s.user);
  const navigate = useNavigate();
  const restructure = useRestructureLoan();

  // Calculate outstanding interest from ledger balances
  const outstandingInterest = useMemo(() => {
    const balances = loan.ledger?.balances ?? [];
    const intBal = balances.find(b => b.indicator === 'interest');
    if (!intBal) return 0;
    return Math.max(0, Number(intBal.debitBalance) - Number(intBal.creditBalance));
  }, [loan.ledger]);

  const currentPrincipal = Number(loan.principal);

  // Form state — pre-filled with current loan terms
  const [principal, setPrincipal] = useState(currentPrincipal.toString());
  const [interestRate, setInterestRate] = useState(Number(loan.interestRate).toString());
  const [rateMode, setRateMode] = useState<'annual' | 'monthly'>('annual');
  const [defaultRate, setDefaultRate] = useState(
    loan.defaultRate ? Number(loan.defaultRate).toString() : '',
  );
  const [termMonths, setTermMonths] = useState(loan.termMonths.toString());
  const [frequency, setFrequency] = useState<PaymentFrequency>(
    normalizeFrequency(loan.paymentFrequency),
  );
  const [reason, setReason] = useState('');
  const [capitaliseInterest, setCapitaliseInterest] = useState(false);
  const [establishmentFee, setEstablishmentFee] = useState('0');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<{
    principal?: string;
    interestRate?: string;
    termMonths?: string;
    defaultRate?: string;
    establishmentFee?: string;
  }>({});

  // Derived values
  const annualRate =
    rateMode === 'annual' ? parseFloat(interestRate) || 0 : (parseFloat(interestRate) || 0) * 12;
  const effectivePrincipal =
    (parseFloat(principal) || 0) + (capitaliseInterest ? outstandingInterest : 0);
  const parsedTermMonths = parseInt(termMonths, 10) || 0;
  const amortizationSummary = useMemo(() => {
    if (effectivePrincipal <= 0 || parsedTermMonths <= 0) return null;

    return calculateAmortization({
      principal: effectivePrincipal,
      annualRate,
      termMonths: parsedTermMonths,
      frequency,
      repaymentType: 'STANDARD',
      summaryTotalsMode: 'EXACT',
    });
  }, [annualRate, effectivePrincipal, frequency, parsedTermMonths]);

  function sanitizeDecimalInput(value: string) {
    return value.replace(/[^0-9.]/g, '');
  }

  function validateDecimal(value: string, options: { min: number; required?: boolean }) {
    if (!value.trim()) {
      return options.required ? 'This field is required.' : '';
    }

    const parsed = Number(value);
    if (!Number.isFinite(parsed)) {
      return 'Enter a valid number.';
    }

    if (parsed < options.min) {
      return `Value must be at least ${options.min}.`;
    }

    return '';
  }

  function validateInteger(value: string, options: { min: number; required?: boolean }) {
    if (!value.trim()) {
      return options.required ? 'This field is required.' : '';
    }

    const parsed = Number.parseInt(value, 10);
    if (!Number.isFinite(parsed)) {
      return 'Enter a valid whole number.';
    }

    if (parsed < options.min) {
      return `Value must be at least ${options.min}.`;
    }

    return '';
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const nextErrors = {
      principal: validateDecimal(principal, { min: 0, required: true }),
      interestRate: validateDecimal(interestRate, { min: 0, required: true }),
      termMonths: validateInteger(termMonths, { min: 1, required: true }),
      defaultRate: defaultRate ? validateDecimal(defaultRate, { min: 0 }) : '',
      establishmentFee: establishmentFee ? validateDecimal(establishmentFee, { min: 0 }) : '',
    };
    setFieldErrors(nextErrors);

    if (Object.values(nextErrors).some(Boolean)) {
      toast.error('Please correct the highlighted fields');
      return;
    }

    if (!reason.trim()) {
      toast.error('Please provide a reason for restructuring');
      return;
    }
    if (!user) return;

    restructure.mutate(
      {
        loanId: loan.id,
        principal: parseFloat(principal) || currentPrincipal,
        interestRate: annualRate,
        defaultRate: defaultRate ? parseFloat(defaultRate) : undefined,
        termMonths: parseInt(termMonths) || loan.termMonths,
        paymentFrequency: frequency,
        reason: reason.trim(),
        userId: user.id,
        capitaliseInterest,
        establishmentFee: parseFloat(establishmentFee) || undefined,
      },
      {
        onSuccess: result => {
          toast.success(`Loan restructured — new loan ${result.newLoan.loanNumber} created`);
          onClose();
          navigate(`/loans/${result.newLoan.id}`);
        },
        onError: (err: any) => {
          handleMutationError(err, 'Failed to restructure loan');
        },
      },
    );
  }

  if (!open) return null;

  return (
    <ModalContentWrapper open={open} onClose={onClose} title="Restructure Loan">
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Warning banner */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-3">
          <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
          <div className="text-sm text-amber-800">
            This will mark <span className="font-semibold">{loan.loanNumber}</span> as RESTRUCTURED
            and create a new loan with the terms below. This action cannot be undone.
          </div>
        </div>

        {/* Current → New comparison header */}
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
            <p className="text-[10px] uppercase tracking-wider text-gray-400 mb-1">Current Terms</p>
            <p className="text-sm font-semibold text-gray-900">{fmt(currentPrincipal)}</p>
            <p className="text-xs text-gray-500">
              {Number(loan.interestRate).toFixed(2)}% · {loan.termMonths} months
            </p>
          </div>
          <div className="bg-indigo-50 rounded-lg p-3 border border-indigo-200">
            <p className="text-[10px] uppercase tracking-wider text-indigo-400 mb-1">New Terms</p>
            <p className="text-sm font-semibold text-indigo-900">{fmt(effectivePrincipal)}</p>
            <p className="text-xs text-indigo-600">
              {annualRate.toFixed(2)}% · {termMonths || '—'} months
            </p>
          </div>
        </div>

        {/* ─── New Principal ─── */}
        <div>
          <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-1.5">
            <DollarSign className="w-3.5 h-3.5 text-gray-400" />
            New Principal
          </label>
          <input
            type="number"
            step="any"
            min="0"
            value={principal}
            onChange={e => {
              setPrincipal(sanitizeDecimalInput(e.target.value));
              setFieldErrors(prev => ({ ...prev, principal: undefined }));
            }}
            onBlur={() =>
              setFieldErrors(prev => ({
                ...prev,
                principal: validateDecimal(principal, { min: 0, required: true }),
              }))
            }
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm
                         focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
          />
          {fieldErrors.principal && (
            <p className="mt-1 text-xs text-red-600">{fieldErrors.principal}</p>
          )}
          {outstandingInterest > 0 && (
            <label className="flex items-center gap-2 mt-2 text-sm text-gray-600 cursor-pointer">
              <input
                type="checkbox"
                checked={capitaliseInterest}
                onChange={e => setCapitaliseInterest(e.target.checked)}
                className="rounded border-gray-300 text-indigo-600 focus:ring-blue-500"
              />
              Capitalise outstanding interest ({fmt(outstandingInterest)}) into new principal
            </label>
          )}
        </div>

        {/* ─── Interest Rate ─── */}
        <div>
          <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-1.5">
            <Percent className="w-3.5 h-3.5 text-gray-400" />
            Interest Rate
          </label>
          <div className="flex gap-2">
            <input
              type="number"
              step="any"
              min="0"
              value={interestRate}
              onChange={e => {
                setInterestRate(sanitizeDecimalInput(e.target.value));
                setFieldErrors(prev => ({ ...prev, interestRate: undefined }));
              }}
              onBlur={() =>
                setFieldErrors(prev => ({
                  ...prev,
                  interestRate: validateDecimal(interestRate, { min: 0, required: true }),
                }))
              }
              className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm
                         focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
            />
            <SelectButtonsNew
              value={rateMode}
              onChange={setRateMode}
              options={[
                { value: 'annual', label: 'Annual' },
                { value: 'monthly', label: 'Monthly' },
              ]}
              classes={{
                container: 'w-auto shrink-0',
                buttonsContainer: 'min-h-0 bg-white border-gray-300',
                button: 'min-w-[84px] whitespace-nowrap px-4 py-2 text-sm font-medium',
              }}
              capitalize={false}
            />
          </div>
          {rateMode === 'monthly' && (
            <p className="text-[11px] text-gray-400 mt-1">= {annualRate.toFixed(2)}% per annum</p>
          )}
          {/* Quick rate buttons */}
          <div className="flex flex-wrap gap-1.5 mt-2">
            {[5, 6, 7, 8, 9, 10, 12, 15, 18, 20, 24].map(r => (
              <Button
                variant="secondary"
                key={r}
                type="button"
                onClick={() => {
                  setRateMode('annual');
                  setInterestRate(r.toString());
                }}
                className={`w-fit px-2 py-1 rounded text-xs border transition-colors ${
                  annualRate === r
                    ? 'border-blue-500 bg-indigo-50 text-blue-700'
                    : 'border-gray-200 text-gray-500 hover:bg-gray-50'
                }`}
              >
                {r}%
              </Button>
            ))}
          </div>
        </div>

        {/* ─── Term & Frequency ─── */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-1.5">
              <Calendar className="w-3.5 h-3.5 text-gray-400" />
              Term (months)
            </label>
            <input
              type="number"
              min="1"
              value={termMonths}
              onChange={e => {
                setTermMonths(e.target.value.replace(/[^\d]/g, ''));
                setFieldErrors(prev => ({ ...prev, termMonths: undefined }));
              }}
              onBlur={() =>
                setFieldErrors(prev => ({
                  ...prev,
                  termMonths: validateInteger(termMonths, { min: 1, required: true }),
                }))
              }
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm
                           focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
            />
            {fieldErrors.termMonths && (
              <p className="mt-1 text-xs text-red-600">{fieldErrors.termMonths}</p>
            )}
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 mb-1.5 block">Frequency</label>
            <select
              value={frequency}
              onChange={e => setFrequency(normalizeFrequency(e.target.value))}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm
                           focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
            >
              {FREQ_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* ─── Reason (required) ─── */}
        <div>
          <label className="text-sm font-medium text-gray-700 mb-1.5 block">
            Reason for Restructure <span className="text-red-500">*</span>
          </label>
          <textarea
            rows={3}
            value={reason}
            onChange={e => setReason(e.target.value)}
            placeholder="e.g. Borrower experiencing cash flow difficulties, agreed to extend term and reduce rate…"
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm
                         focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none resize-none"
          />
        </div>

        {/* ─── Advanced options ─── */}
        <button
          type="button"
          onClick={() => setShowAdvanced(!showAdvanced)}
          className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700"
        >
          {showAdvanced ? (
            <ChevronUp className="w-3.5 h-3.5" />
          ) : (
            <ChevronDown className="w-3.5 h-3.5" />
          )}
          Advanced Options
        </button>

        {showAdvanced && (
          <div className="space-y-4 pl-4 border-l-2 border-gray-100">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                Default Rate (%)
              </label>
              <input
                type="number"
                step="any"
                min="0"
                value={defaultRate}
                onChange={e => {
                  setDefaultRate(sanitizeDecimalInput(e.target.value));
                  setFieldErrors(prev => ({ ...prev, defaultRate: undefined }));
                }}
                onBlur={() =>
                  setFieldErrors(prev => ({
                    ...prev,
                    defaultRate: defaultRate ? validateDecimal(defaultRate, { min: 0 }) : '',
                  }))
                }
                placeholder="Leave empty to keep current"
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm
                             focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
              />
              {fieldErrors.defaultRate && (
                <p className="mt-1 text-xs text-red-600">{fieldErrors.defaultRate}</p>
              )}
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                Establishment Fee
              </label>
              <input
                type="number"
                step="any"
                min="0"
                value={establishmentFee}
                onChange={e => {
                  setEstablishmentFee(sanitizeDecimalInput(e.target.value));
                  setFieldErrors(prev => ({ ...prev, establishmentFee: undefined }));
                }}
                onBlur={() =>
                  setFieldErrors(prev => ({
                    ...prev,
                    establishmentFee: establishmentFee
                      ? validateDecimal(establishmentFee, { min: 0 })
                      : '',
                  }))
                }
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm
                             focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
              />
              {fieldErrors.establishmentFee && (
                <p className="mt-1 text-xs text-red-600">{fieldErrors.establishmentFee}</p>
              )}
            </div>
          </div>
        )}

        {/* ─── Summary ─── */}
        <div className="bg-gray-50 rounded-lg border border-gray-200 p-4 space-y-2">
          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
            New Loan Summary
          </h4>
          <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-500">Principal</span>
              <span className="font-medium text-gray-900">{fmt(effectivePrincipal)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Rate</span>
              <span className="font-medium text-gray-900">{annualRate.toFixed(2)}% p.a.</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Monthly Payment</span>
              <span className="font-medium text-gray-900">
                {fmtPrecise(amortizationSummary?.monthlyPayment ?? 0)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Total Interest</span>
              <span className="font-medium text-gray-900">
                {fmtPrecise(amortizationSummary?.totalInterest ?? 0)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Total Repayment</span>
              <span className="font-medium text-indigo-600">
                {fmtPrecise(amortizationSummary?.totalPayment ?? 0)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Term</span>
              <span className="font-medium text-gray-900">{termMonths} months</span>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-md border border-gray-300 text-sm font-medium text-gray-700
                         hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={restructure.isPending || !reason.trim()}
            className="flex items-center gap-2 px-4 py-2 rounded-md bg-amber-600 text-white text-sm
                         font-medium hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed
                         transition-colors"
          >
            <RefreshCcw className={`w-4 h-4 ${restructure.isPending ? 'animate-spin' : ''}`} />
            Restructure Loan
          </button>
        </div>
      </form>
    </ModalContentWrapper>
  );
}
